import { useState, type FormEvent } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { companyInfo } from '@/mocks/navigation';
import { testimonials } from '@/mocks/homepage';

const FORM_SUBMIT_URL = 'https://readdy.ai/api/form/d9u4lnt3pcjqs2fcsm5g';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');
    setFormMessage('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypotValue = (formData.get('company_alt') as string || '').trim();
    if (honeypotValue) {
      setFormStatus('success');
      setFormMessage('感谢您的留言，我们会尽快回复！');
      form.reset();
      return;
    }

    formData.delete('company_alt');

    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        body.append(key, value as string);
      });

      const response = await fetch(FORM_SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed && parsed.code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢您的留言，我们会尽快回复！');
        form.reset();
      } else {
        const serverMsg =
          (parsed && (parsed.meta as Record<string, unknown>)?.message as string) ||
          (parsed && (parsed.meta as Record<string, unknown>)?.detail as string) ||
          responseText ||
          '提交失败，请稍后再试';
        if (typeof serverMsg === 'string' && serverMsg.includes('spam')) {
          setFormStatus('error');
          setFormMessage('提交失败，请稍后再试');
        } else {
          setFormStatus('error');
          setFormMessage(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后再试');
        }
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络异常，请检查网络后重试');
    }
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: `关于${companyInfo.name}`,
            description: companyInfo.slogan,
            url: 'https://jointdraft.github.io/about',
          }),
        }}
      />
      <Navbar />
      <main>
        <section className="pt-24 pb-14 bg-background-100/60">
          <div className="w-full px-6 md:px-10 max-w-4xl mx-auto text-center">
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4">
              关于一起草
            </h1>
            <p className="text-foreground-600 text-base max-w-2xl mx-auto">
              {companyInfo.slogan}
            </p>
          </div>
        </section>

        <section className="w-full px-6 md:px-10 py-16 md:py-20 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-5">我们的故事</h2>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  一起草诞生于一个简单的观察：团队协作起草文档这件事，为什么如此低效？
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  我们的创始团队来自企业服务领域，亲身经历了无数次的文档协作痛点——版本邮件满天飞、修改意见散落在微信群里、最终版本不知道到底是谁改了什么。这些经历让我们坚信，团队需要一款真正好用的协同起草工具。
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  2024年，一起草正式上线。我们专注于一个核心使命：让团队协作起草更高效、更顺畅。从最初的几个人到今天的50万+活跃用户，一起草已经在全球200多个国家和地区服务了超过10,000家企业客户。
                </p>
              </div>
              <div className="rounded-lg overflow-hidden h-[320px]">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20collaborative%20office%20with%20diverse%20team%20working%20happily%20together%2C%20warm%20natural%20light%2C%20plants%20and%20creative%20space%2C%20genuine%20teamwork%20atmosphere%2C%20editorial%20photography%20with%20soft%20shadows&width=600&height=400&seq=about-story&orientation=landscape"
                  alt="一起草团队"
                  className="w-full h-full object-cover"
                  width={600}
                  height={400}
                />
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-6 md:px-10 py-16 md:py-20 bg-background-100/60">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-10 text-center">用户口碑</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {testimonials.map((testimonial, index) => (
                <div
                  key={index}
                  className="p-6 rounded-lg bg-background-50 border border-background-200/60"
                >
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-6">
                    "{testimonial.content}"
                  </p>
                  <div className="flex items-center gap-3">
                    <img
                      src={testimonial.avatar}
                      alt={testimonial.name}
                      className="w-10 h-10 rounded-full object-cover"
                      width={40}
                      height={40}
                    />
                    <div>
                      <div className="text-sm font-semibold text-foreground-950">{testimonial.name}</div>
                      <div className="text-xs text-foreground-500">{testimonial.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="contact" className="w-full px-6 md:px-10 py-16 md:py-20 bg-background-50">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-3">联系我们</h2>
              <p className="text-foreground-600 text-sm">有任何问题或合作意向？我们期待您的来信。</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              <div className="text-center p-6 rounded-lg bg-background-100/70">
                <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-primary-100 text-primary-500 mx-auto mb-4">
                  <i className="ri-mail-line text-xl"></i>
                </div>
                <h3 className="font-heading text-sm font-bold text-foreground-950 mb-2">电子邮箱</h3>
                <p className="text-sm text-foreground-600">{companyInfo.email}</p>
              </div>
              <div className="text-center p-6 rounded-lg bg-background-100/70">
                <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-primary-100 text-primary-500 mx-auto mb-4">
                  <i className="ri-phone-line text-xl"></i>
                </div>
                <h3 className="font-heading text-sm font-bold text-foreground-950 mb-2">联系电话</h3>
                <p className="text-sm text-foreground-600">{companyInfo.phone}</p>
              </div>
              <div className="text-center p-6 rounded-lg bg-background-100/70">
                <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-primary-100 text-primary-500 mx-auto mb-4">
                  <i className="ri-map-pin-line text-xl"></i>
                </div>
                <h3 className="font-heading text-sm font-bold text-foreground-950 mb-2">公司地址</h3>
                <p className="text-sm text-foreground-600">{companyInfo.address}</p>
              </div>
            </div>

            <form onSubmit={handleSubmit} data-readdy-form="">
              <div className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                    <input
                      type="text"
                      id="contact-name"
                      name="name"
                      className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                      placeholder="您的姓名"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                    <input
                      type="email"
                      id="contact-email"
                      name="email"
                      className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                      placeholder="your@email.com"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="contact-company" className="block text-sm font-medium text-foreground-700 mb-1.5">公司名称</label>
                  <input
                    type="text"
                    id="contact-company"
                    name="company"
                    className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                    placeholder="您的公司"
                  />
                </div>

                <div className="form-spam-trap">
                  <label htmlFor="company-alt">备用联系方式</label>
                  <input
                    type="text"
                    id="company-alt"
                    name="company_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                </div>

                <div>
                  <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言内容</label>
                  <textarea
                    id="contact-message"
                    name="message"
                    rows={5}
                    maxLength={500}
                    className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                    placeholder="请输入您的留言，我们将尽快回复（最多500字）"
                    required
                  ></textarea>
                  <p className="text-xs text-foreground-400 mt-1">最多500字符</p>
                </div>

                {formStatus === 'success' && (
                  <div className="p-4 rounded-md bg-accent-100 text-accent-800 text-sm font-medium">
                    <i className="ri-check-line mr-2"></i>{formMessage}
                  </div>
                )}

                {formStatus === 'error' && (
                  <div className="p-4 rounded-md bg-primary-100 text-primary-800 text-sm font-medium">
                    <i className="ri-error-warning-line mr-2"></i>{formMessage}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={formStatus === 'submitting'}
                  className="w-full sm:w-auto whitespace-nowrap px-8 py-3 bg-primary-500 text-background-50 font-semibold rounded-md hover:bg-primary-600 transition-all duration-200 cursor-pointer text-sm disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {formStatus === 'submitting' ? '发送中...' : '发送留言'}
                </button>
              </div>
            </form>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}